import type { Country, CountryCode, CountryMetrics } from '@/data';
import type {
  Answer,
  Answers,
  CountryMatchResult,
  Dimension,
  QuestionId,
  QuizResult,
  ScoringWeights,
} from './types';
import { getPassport } from './types';
import {
  BUDGET_BUCKETS,
  DEFAULT_WEIGHTS,
  DIMENSIONS,
  QUESTIONS_BY_ID,
  QUIZ_SCHEMA_VERSION,
  SEISMIC_COUNTRIES,
} from './schema';
import { scoreVisaPathway, type CountryVisaExtraFile } from './visa-extra';
import { scoreTaxIntent, type CountryTaxRegimesFile } from './tax-regimes';
import { getHouseholdTotal, type CostOfLivingFile } from './cost-of-living';
import { deriveHouseholdBucket } from './derive';

// Floor on each component so a single zero does not collapse the geometric mean.
const COMPONENT_FLOOR = 0.05;
const TOP_N_DEFAULT = 5;

// Sigmoid penalty (smooth, no cliff). delta is how far over a threshold.
function sigmoid(delta: number, steepness: number, offset = 0): number {
  return 1 / (1 + Math.exp(-(delta + offset) * steepness));
}

function clamp01(v: number): number {
  if (Number.isNaN(v)) return 0;
  if (v < 0) return 0;
  if (v > 1) return 1;
  return v;
}

function getAnswer(answers: Answers, q: QuestionId): Answer | undefined {
  return answers[q];
}

function hasOption(answers: Answers, q: QuestionId, optionId: string): boolean {
  const a = getAnswer(answers, q);
  return !!a && a.optionIds.includes(optionId);
}

// Walk all picked options and accumulate implicit weight boosts on top of defaults.
export function deriveWeights(answers: Answers): ScoringWeights {
  const out: ScoringWeights = { ...DEFAULT_WEIGHTS };
  for (const qid of Object.keys(answers) as QuestionId[]) {
    const answer = answers[qid];
    if (!answer) continue;
    const q = QUESTIONS_BY_ID[qid];
    if (!q?.options) continue;
    for (const optId of answer.optionIds) {
      const opt = q.options.find((o) => o.id === optId);
      if (!opt?.weights) continue;
      for (const [dim, w] of Object.entries(opt.weights) as [Dimension, number][]) {
        out[dim] = (out[dim] ?? 0) + w;
      }
    }
  }
  // Family weight remains low unless the user signalled family context.
  const hasKids =
    hasOption(answers, 'family', 'kids-preschool') || hasOption(answers, 'family', 'kids-school');
  if (!hasKids) out.family = DEFAULT_WEIGHTS.family;
  // Budget picked → cost matters more; family with kids cares even more about cost-of-life.
  if (answers.budget?.budgetBucket) {
    out.cost = out.cost + (hasKids ? 1.0 : 0.5);
  }
  return out;
}

// Hard exclusion rules — return human-readable reasons (empty array = passes).
export function applyHardFilters(country: Country, answers: Answers): string[] {
  const reasons: string[] = [];
  const m = country.metrics;
  if (!m) return reasons; // no metrics → handled separately as `unscored`

  if (hasOption(answers, 'dealbreakers', 'complex-visa')) {
    if (!m.visa.digital_nomad && !m.visa.freelance_path) {
      reasons.push('no_digital_nomad_or_freelance_path');
    }
  }
  if (hasOption(answers, 'dealbreakers', 'expensive-healthcare')) {
    if (m.healthcare.uhc_index < 70) reasons.push('healthcare_below_70');
  }
  if (hasOption(answers, 'dealbreakers', 'hot-summer')) {
    // Hard-exclude only when the representative value is extreme.
    // Countries with ranges (e.g. Italy) are handled as soft penalties.
    const hasRange = !!m.climate.jul_max_range;
    const hotThreshold = hasRange ? 38 : 35;
    if (m.climate.jul_max_c > hotThreshold) reasons.push('summer_above_35c');
  }
  if (hasOption(answers, 'dealbreakers', 'snow')) {
    if (m.climate.jan_min_range) {
      // Country has regional diversity. Only hard-exclude if even the warmest
      // escape zone gets frost — meaning there is no warm-winter region at all.
      // e.g. Italy jan_min_range[-15, 8]: warmest region = +8°C → not excluded.
      if (m.climate.jan_min_range[1] < 0) reasons.push('winter_below_0c');
      // else: coldest region has frost but warmest doesn't → soft penalty only
    } else {
      if (m.climate.jan_min_c < 0) reasons.push('winter_below_0c');
    }
  }
  // Note: budget is intentionally NOT a hard dealbreaker. The dataset
  // stores `monthly_eur_family` (family of ~4), so even a "single person
  // on €500/mo" picks bucket 1 and any country with family-cost > 700 —
  // which is every country in the dataset — used to be excluded, leaving
  // the user with zero matches and no recovery path. Cost mismatch is
  // now handled exclusively by scoreCost() as a soft penalty so the user
  // still gets ranked candidates with an honest cost warning.
  return reasons;
}

// Climate component: best fit if any user-selected type matches country tags.
function scoreClimate(metrics: CountryMetrics, answers: Answers): number {
  const ans = getAnswer(answers, 'climate');
  if (!ans || ans.optionIds.length === 0) return 0.6;
  if (ans.optionIds.includes('any')) return 0.8;
  const wanted = new Set(ans.optionIds);
  const tags = new Set(metrics.climate.type as readonly string[]);
  let overlap = 0;
  for (const t of wanted) if (tags.has(t)) overlap += 1;
  if (overlap === 0) return 0.2;
  return clamp01(0.6 + 0.3 * overlap);
}

// Multiplier on country-average cost depending on where in the country the user
// intends to live. Handles multi-select (average of each selected option).
const URBAN_COST_MULT: Record<string, number> = {
  capital: 1.3,
  mid_city: 1.0,
  town: 0.7,
  coastal: 1.1,
};
function urbanCostMultiplier(answers: Answers): number {
  const opts = answers.urban_density?.optionIds ?? [];
  if (opts.length === 0) return 1.0;
  const sum = opts.reduce((acc, id) => acc + (URBAN_COST_MULT[id] ?? 1.0), 0);
  return sum / opts.length;
}

// Cost component: smooth taper penalty for overshooting the user's budget cap.
//
// 2026-08-18: reads the household-specific total from cost_of_living.json
// when it's loaded and covers this country (household bucket derived from
// the `family` + `family_children_count` answers — see
// deriveHouseholdBucket()). Falls back to the legacy
// `metrics.cost.monthly_eur_family` figure otherwise — same
// null-means-fall-back contract as scoreVisaPathway()/scoreTaxIntent().
function scoreCost(
  metrics: CountryMetrics,
  answers: Answers,
  countryCode?: string,
  costFile?: CostOfLivingFile,
): number {
  const bucket = answers.budget?.budgetBucket;
  if (!bucket) return 0.6; // user skipped — neutral
  const def = BUDGET_BUCKETS.find((b) => b.id === bucket);
  if (!def) return 0.6;
  const cap = def.max_eur === Number.POSITIVE_INFINITY ? 5000 : def.max_eur;

  const householdTotal = countryCode
    ? getHouseholdTotal(costFile, countryCode, deriveHouseholdBucket(answers))
    : null;
  const baseCost = householdTotal ?? metrics.cost.monthly_eur_family;
  const cost = baseCost * urbanCostMultiplier(answers);

  const hasKids =
    hasOption(answers, 'family', 'kids-preschool') || hasOption(answers, 'family', 'kids-school');
  // The 1.8x over-budget kicker existed to compensate for every household
  // sharing ONE "family of ~4" figure — a solo user with kids was scored
  // against a number already inflated for them, so overage was punished
  // harder as a rough proxy for "this is worse than it looks for a family".
  // When we have the real per-household total that compensation is no
  // longer needed — the number is already correct for this household.
  const familyMult = householdTotal != null ? 1.0 : (hasKids ? 1.8 : 1.0);
  if (cost <= cap) return clamp01(1 - 0.3 * (cost / cap));
  // Use cost/cap ratio with quadratic decay for over-budget — punishes >2x heavily.
  const ratio = (cost / cap) * familyMult;
  return clamp01(0.8 / (ratio * ratio));
}

// Visa component: bonus for available pathways for the user's profile.
// Passport-aware: EU passport inside EU/Schengen = free movement (perfect score).
// `open_to_ru` only penalises CIS passports — neutral for everyone else.
function scoreVisa(
  metrics: CountryMetrics,
  answers: Answers,
  countryCode?: string,
  visaFile?: CountryVisaExtraFile,
): number {
  const passport = getPassport(answers);

  // EU passport in EU/Schengen country = free movement, no visa needed.
  if (passport === 'eu' && metrics.visa.schengen) return 1.0;

  // Phase 3: use detailed visa pathway data when available.
  if (visaFile && countryCode) {
    const selectedPathways = (answers.visa_pathway?.optionIds ?? []) as Parameters<typeof scoreVisaPathway>[1]['selectedPathways'];
    if (selectedPathways.length > 0) {
      const pathwayScore = scoreVisaPathway(visaFile, {
        countryCode,
        selectedPathways,
        passport: passport ?? undefined,
        urgent: hasOption(answers, 'horizon', 'now'),
      });
      if (pathwayScore !== null) return pathwayScore;
    }
  }

  // Legacy scoring (metrics.visa boolean flags) — fallback when no pathway
  // selected or visa_extra data not yet loaded.
  let score = 0.5;
  if (metrics.visa.digital_nomad) score += 0.2;
  if (metrics.visa.freelance_path) score += 0.15;
  if (metrics.visa.years_to_citizenship != null && metrics.visa.years_to_citizenship <= 5) {
    score += 0.15;
  }
  if (hasOption(answers, 'horizon', 'now')) {
    if (!metrics.visa.digital_nomad && !metrics.visa.freelance_path) score -= 0.2;
  }

  // Post-Soviet passport: countries closed to RU applicants take a heavy hit.
  // Same treatment for BY (sanctions parallel); UA users have Temporary
  // Protection, so we don't penalise them here — passport-treatment dataset
  // handles UA's situation separately in scoreCountries().
  if (passport === 'ru' || passport === 'by' || passport === undefined) {
    if (!metrics.visa.open_to_ru) score -= 0.3;
  }
  // Anglo/Other passports: open_to_ru is irrelevant — small neutral bonus
  // for any open visa policy already covered by DN/freelance/citizenship checks.

  return clamp01(score);
}

// Tax component: low PIT or special regime score higher; passive income cares about worldwide taxation.
function scoreTax(
  metrics: CountryMetrics,
  answers: Answers,
  countryCode?: string,
  taxFile?: CountryTaxRegimesFile,
): number {
  // Phase 3: use tax intent scoring when data + quiz answer are available.
  if (taxFile && countryCode) {
    const intent = answers.tax_residency_intent?.optionIds[0] as Parameters<typeof scoreTaxIntent>[2];
    const incomeTypes = answers.income?.optionIds;
    const intentScore = scoreTaxIntent(taxFile, countryCode, intent, incomeTypes);
    if (intentScore !== null) {
      // Blend intent score (60%) with legacy rate-based score (40%) so we
      // don't completely lose the "low PIT is generically good" signal.
      const rateScore = clamp01(1 - metrics.tax.pit_top_rate_pct / 75);
      return clamp01(intentScore * 0.6 + rateScore * 0.4);
    }
  }

  // Legacy scoring (metrics.tax fields) — fallback when file not loaded or
  // country not in dataset.
  let score = clamp01(1 - metrics.tax.pit_top_rate_pct / 75);
  if (metrics.tax.flat_freelance_pct != null && metrics.tax.flat_freelance_pct <= 15) {
    score = Math.max(score, 0.85);
  }
  if (hasOption(answers, 'income', 'passive') && metrics.tax.worldwide_taxation) {
    score *= 0.7;
  }
  if (hasOption(answers, 'income', 'business') && metrics.tax.flat_freelance_pct == null) {
    score *= 0.85;
  }
  // Local income earners care less about top PIT — give a floor.
  if (hasOption(answers, 'income', 'local')) {
    score = Math.max(score, 0.55);
  }
  return clamp01(score);
}

function scoreHealthcare(metrics: CountryMetrics): number {
  return clamp01(metrics.healthcare.uhc_index / 100);
}

function scoreSafety(metrics: CountryMetrics): number {
  // GPI: 1.0 (best) … 4.0 (worst). Map linearly to 0..1.
  const gpi = metrics.safety.gpi;
  return clamp01(1 - (gpi - 1.0) / 3.0);
}

const BAND_SCORE: Record<string, number> = {
  very_high: 1.0,
  high: 0.85,
  moderate: 0.55,
  low: 0.3,
};

function scoreLanguage(metrics: CountryMetrics, answers: Answers): number {
  const langs = getAnswer(answers, 'languages')?.optionIds ?? [];
  const official = new Set(metrics.language.official.map((l) => l.toLowerCase()));
  let score = BAND_SCORE[metrics.language.english_band] ?? 0.5;
  // Speaking the local language is a strong boost.
  for (const l of langs) {
    if (l === 'any') {
      score = Math.max(score, 0.85);
      continue;
    }
    if (l === 'ru-only') {
      // RU-only: only good if banking/community works AND English is high.
      score *= metrics.language.english_band === 'very_high' || metrics.language.english_band === 'high' ? 0.85 : 0.45;
      continue;
    }
    if (official.has(l)) score = Math.max(score, 0.95);
  }
  // Boost if user signed up for "any language".
  if (langs.includes('any')) score = Math.max(score, 0.8);
  // Urban density modifies effective language barrier: capitals are more
  // anglophone; small towns force engagement with the local language.
  if (hasOption(answers, 'urban_density', 'capital')) score = Math.min(1, score + 0.1);
  if (hasOption(answers, 'urban_density', 'town')) score = Math.max(0, score - 0.1);
  return clamp01(score);
}

function scoreJobs(metrics: CountryMetrics, answers: Answers): number {
  const mbps = metrics.jobs.internet_mbps;
  // 50 Mbps is "enough" for non-remote; remote workers care about the upper end.
  const cap = hasOption(answers, 'income', 'remote') ? 200 : 50;
  let score = clamp01(Math.min(mbps, cap) / cap);
  if (hasOption(answers, 'income', 'remote') && mbps < 50) score *= 0.6;
  return clamp01(score);
}

function scoreFamily(metrics: CountryMetrics, answers: Answers): number {
  const hasKids =
    hasOption(answers, 'family', 'kids-preschool') || hasOption(answers, 'family', 'kids-school');
  let score = clamp01(metrics.family.school_quality);
  if (hasKids) {
    if (metrics.family.english_schools) score = Math.max(score, 0.75);
    else score *= 0.7;
  } else {
    // No kids — family dimension is mostly inactive, neutral score.
    score = Math.max(score, 0.6);
  }
  return clamp01(score);
}

// Banking component — passport-aware.
// RU/BY hit SWIFT / sanctions friction: rely on country.metrics.banking
// .ru_passport tier. UA passport holders have Temporary Protection in EU
// (full banking access), so we don't penalise them with this signal — the
// passport-treatment dataset handles their actual situation per-country.
// EU/Anglo/Other passports open accounts globally with minor variation
// by passport prestige.
function scoreBanking(metrics: CountryMetrics, answers: Answers): number {
  const passport = getPassport(answers);
  const tier = metrics.banking.ru_passport;

  if (passport === 'ru' || passport === 'by') {
    if (tier === 'yes') return 1.0;
    if (tier === 'limited') return 0.5;
    return 0.1;
  }
  if (passport === 'ua') return 0.85; // EU Temporary Protection covers banking access
  if (passport === 'eu') return 0.95;
  if (passport === 'anglo') return 0.85;
  if (passport === 'other') return 0.75;

  // Defensive: passport not answered → fall back to legacy RU-context heuristic
  // so older tests/saved sessions keep their prior behavior.
  const ruRelevant =
    hasOption(answers, 'languages', 'ru-only') ||
    hasOption(answers, 'priorities', 'ru-community') ||
    hasOption(answers, 'income', 'passive');
  if (!ruRelevant) return 0.8;
  if (tier === 'yes') return 1.0;
  if (tier === 'limited') return 0.55;
  return 0.2;
}

const SCORERS: Record<Dimension, (m: CountryMetrics, a: Answers) => number> = {
  climate: scoreClimate,
  cost: scoreCost,
  visa: scoreVisa,
  tax: scoreTax,
  healthcare: (m) => scoreHealthcare(m),
  safety: (m) => scoreSafety(m),
  language: scoreLanguage,
  jobs: scoreJobs,
  family: scoreFamily,
  banking: scoreBanking,
};

export function scoreComponents(
  country: Country,
  answers: Answers,
  visaFile?: CountryVisaExtraFile,
  taxFile?: CountryTaxRegimesFile,
  costFile?: CostOfLivingFile,
): Partial<Record<Dimension, number>> {
  const m = country.metrics;
  if (!m) return {};
  const out: Partial<Record<Dimension, number>> = {};
  for (const dim of DIMENSIONS) {
    if (dim === 'visa') {
      out.visa = clamp01(scoreVisa(m, answers, country.code, visaFile));
    } else if (dim === 'tax') {
      out.tax = clamp01(scoreTax(m, answers, country.code, taxFile));
    } else if (dim === 'cost') {
      out.cost = clamp01(scoreCost(m, answers, country.code, costFile));
    } else {
      out[dim] = clamp01(SCORERS[dim](m, answers));
    }
  }
  return out;
}

// Soft penalties: 0..1 total. Combined multiplicatively with base.
export function computeSoftPenalties(
  country: Country,
  answers: Answers,
): { total: number; reasons: string[] } {
  const m = country.metrics;
  if (!m) return { total: 0, reasons: [] };
  const reasons: string[] = [];
  let total = 0;
  if (hasOption(answers, 'dealbreakers', 'humidity') && m.climate.humidity_pct > 70) {
    const delta = (m.climate.humidity_pct - 70) / 30;
    const p = 0.4 * sigmoid(delta, 4, -0.5);
    total += p;
    reasons.push('humidity_above_70');
  }
  if (hasOption(answers, 'dealbreakers', 'earthquakes') && SEISMIC_COUNTRIES.has(country.code)) {
    total += 0.25;
    reasons.push('seismic_country');
  }
  if (hasOption(answers, 'dealbreakers', 'hot-summer')) {
    const hotPeak = m.climate.jul_max_range?.[1] ?? m.climate.jul_max_c;
    const coolFloor = m.climate.jul_max_range?.[0];
    const rep = m.climate.jul_max_c;
    // If the country has cool escape zones (Alps, mountains) AND the user
    // explicitly prefers that climate, the hot penalty is nearly irrelevant —
    // they can live in the cool part without experiencing the hot regions.
    const hasAlpineEscape =
      coolFloor != null &&
      coolFloor <= 25 &&
      (hasOption(answers, 'climate', 'alps') || hasOption(answers, 'climate', 'continental'));
    if (hotPeak > 35 && rep <= 38) {
      total += hasAlpineEscape ? 0.07 : 0.2;
      reasons.push('warm_summer');
    } else if (rep > 30 && rep <= 38) {
      total += hasAlpineEscape ? 0.05 : 0.15;
      reasons.push('warm_summer');
    }
  }
  if (hasOption(answers, 'dealbreakers', 'snow')) {
    // Soft penalty when country has cold/snowy regions but also warm escape zones.
    // (Hard exclusion already handled in applyHardFilters for uniformly cold countries.)
    const coldestWinter = m.climate.jan_min_range?.[0] ?? m.climate.jan_min_c;
    const warmestWinter = m.climate.jan_min_range?.[1];
    if (coldestWinter < 0 && warmestWinter != null && warmestWinter >= 0) {
      total += 0.1;
      reasons.push('partial_snow_risk');
    }
  }
  // ru-only without english support is a soft penalty (hard exclusion would be too strict).
  if (hasOption(answers, 'languages', 'ru-only')) {
    const band = m.language.english_band;
    if (band === 'low') {
      total += 0.25;
      reasons.push('low_english_for_ru_only');
    } else if (band === 'moderate') {
      total += 0.12;
      reasons.push('moderate_english_for_ru_only');
    }
  }
  // RU/BY banking friction — UA holders skip this (Temporary Protection).
  // Legacy fallback: if passport question was skipped but RU context signals
  // are present, apply the same penalty so older saved sessions still warn.
  const passport = getPassport(answers);
  const ruContext =
    hasOption(answers, 'languages', 'ru-only') || hasOption(answers, 'priorities', 'ru-community');
  const treatAsRuBy =
    passport === 'ru' || passport === 'by' || (passport === undefined && ruContext);
  if (treatAsRuBy && m.banking.ru_passport === 'no') {
    total += 0.3;
    reasons.push('banking_closed_for_ru');
  }
  return { total: clamp01(total), reasons };
}

// Geometric weighted mean. Each component is floored to avoid zero-collapse.
function geometricMean(
  components: Partial<Record<Dimension, number>>,
  weights: ScoringWeights,
): number {
  let totalW = 0;
  for (const dim of DIMENSIONS) totalW += weights[dim] ?? 0;
  if (totalW <= 0) return 0;
  let logSum = 0;
  for (const dim of DIMENSIONS) {
    const w = weights[dim] ?? 0;
    if (w <= 0) continue;
    const c = Math.max(components[dim] ?? 0, COMPONENT_FLOOR);
    logSum += (w / totalW) * Math.log(c);
  }
  return Math.exp(logSum);
}

// Pick top 1 blocker reason to surface on the result card.
function pickBlockers(
  components: Partial<Record<Dimension, number>>,
  penalties: { reasons: string[] },
): string[] {
  const blockers: string[] = [];
  let worstDim: Dimension | null = null;
  let worstVal = 1;
  for (const dim of DIMENSIONS) {
    const v = components[dim];
    if (v == null) continue;
    if (v < worstVal) {
      worstVal = v;
      worstDim = dim;
    }
  }
  if (worstDim && worstVal < 0.45) blockers.push(`low_${worstDim}`);
  if (penalties.reasons.length > 0 && blockers.length === 0) {
    blockers.push(penalties.reasons[0]);
  }
  return blockers;
}

export function scoreCountry(
  country: Country,
  answers: Answers,
  weights: ScoringWeights = DEFAULT_WEIGHTS,
  visaFile?: CountryVisaExtraFile,
  taxFile?: CountryTaxRegimesFile,
  costFile?: CostOfLivingFile,
): CountryMatchResult {
  const hardFails = applyHardFilters(country, answers);
  const components = scoreComponents(country, answers, visaFile, taxFile, costFile);
  const penalties = computeSoftPenalties(country, answers);

  if (hardFails.length > 0) {
    return {
      countryCode: country.code,
      score: 0,
      excluded: true,
      exclusionReasons: hardFails,
      components,
      penalties,
      blockers: hardFails.slice(0, 1),
    };
  }

  const base = geometricMean(components, weights);
  let final = clamp01(base * (1 - penalties.total));

  // Tie bonus: +10% for countries the user explicitly named (family,
  // friends, language, prior residency). Per-country signal applied AFTER
  // penalties so a hard-penalised country can't leapfrog a clean match on
  // ties alone. 10% is large enough to surface a personally meaningful
  // destination into the top 3 when it is otherwise close, but not so
  // dominant that it overrides a dramatically better objective fit.
  if (hasOption(answers, 'ties', country.code)) {
    final = clamp01(final + 0.10);
  }

  return {
    countryCode: country.code,
    score: final,
    excluded: false,
    components,
    penalties,
    blockers: pickBlockers(components, penalties),
  };
}

// Stage 4.7 — opaque interface to avoid a circular import; the real shape
// lives in features/quiz/passport-treatment.ts. We only need lookup access.
interface PassportTreatmentLookup {
  countries: Record<
    string,
    {
      ru: string;
      by: string;
      ua: string;
      /** Schema v1 (legacy) — kept optional for cached payloads. */
      evidence?: string;
      /** Schema v2 — per-language evidence. */
      evidence_en?: string;
      evidence_ru?: string;
    }
  >;
}

export function scoreCountries(
  countries: Country[],
  answers: Answers,
  topN: number = TOP_N_DEFAULT,
  passportTreatmentFile?: PassportTreatmentLookup,
  visaFile?: CountryVisaExtraFile,
  taxFile?: CountryTaxRegimesFile,
  costFile?: CostOfLivingFile,
): QuizResult {
  const weights = deriveWeights(answers);
  const passport = getPassport(answers);
  const results: CountryMatchResult[] = [];
  const unscored: CountryCode[] = [];
  for (const c of countries) {
    if (!c.metrics) {
      unscored.push(c.code);
      continue;
    }
    const result = scoreCountry(c, answers, weights, visaFile, taxFile, costFile);
    applyPassportTreatment(result, c.code, passport, passportTreatmentFile);
    results.push(result);
  }
  const excluded = results.filter((r) => r.excluded);
  const eligible = results
    .filter((r) => !r.excluded)
    .sort((a, b) => b.score - a.score);
  return {
    topMatches: eligible.slice(0, topN),
    excluded,
    unscored,
    weights,
    generatedAt: new Date().toISOString(),
    schemaVersion: QUIZ_SCHEMA_VERSION,
  };
}

// Stage 4.7 — fold per-country de-facto passport treatment into the result.
// `hostile` → hard-exclude (banks closed, notaries refusing); `restrictive`
// → -30% score multiplier; `cautious` → small -5% nudge + blocker hint so
// the UI shows an "extra due diligence" notice; `welcoming` → no-op.
//
// Only applies to ru/by/ua passports — for everyone else this is a no-op.
function applyPassportTreatment(
  result: CountryMatchResult,
  countryCode: string,
  passport: ReturnType<typeof getPassport>,
  file: PassportTreatmentLookup | undefined,
): void {
  if (!file) return;
  if (passport !== 'ru' && passport !== 'by' && passport !== 'ua') return;
  const entry = file.countries[countryCode.toUpperCase()];
  if (!entry) return;
  const status = entry[passport] as 'welcoming' | 'cautious' | 'restrictive' | 'hostile';

  // Tag the result so the UI can surface a passport-specific notice — even
  // for `welcoming` / `cautious` we want the badge present so the user
  // understands that we DID consider their passport, not skipped it.
  result.blockers.push(`passport_treatment_${status}`);

  if (status === 'hostile') {
    result.excluded = true;
    result.exclusionReasons = [
      ...(result.exclusionReasons ?? []),
      `passport_hostile_${passport}`,
    ];
    return;
  }

  let multiplier = 1;
  if (status === 'restrictive') multiplier = 0.7;
  else if (status === 'cautious') multiplier = 0.95;

  if (multiplier < 1) {
    result.score = result.score * multiplier;
    result.penalties.reasons.push(`passport_${status}_${passport}`);
  }
}
